import React, { useEffect, useMemo, useState } from 'react';
import * as globalMath from 'mathjs';
import { DataApi } from 'configuration-schemas/confdefnView';
import { zEditableOverlay, EditableOverlayField } from 'configuration-schemas/viewdefns/viewdefn';
import { BasicPivotItem } from 'src/worker/pivotWorker.types';
import Axios from 'src/services/axios';
import {
  ColumnDetailsConfig,
  DividedColumnDetailsSectionColumn,
  ValueSelection,
} from 'src/pages/AssortmentBuild/StyleEdit/DividedColumnDetailsSection/DividedColumnDetails.types';
import { columnConfigToEditableField } from 'src/pages/AssortmentBuild/StyleEdit/DividedColumnDetailsSection/DividedColumnDetails.utils';
import SquareActionButton from 'src/common-ui/components/Buttons/SquareActionButton';
import { Overlay } from 'src/common-ui/index';
import { logError } from 'src/services/loggingService';
import styles from './EditableOverlay.styles';

export interface EditableOverlayOwnProps {
  configApi: any;
}

export interface EditableOverlayDispatchProps {
  /**
   * Called when the user submits. `scope` is a flat map of `dataIndex -> entered value`
   * that the host injects into `dataApi`'s params (via `processApiParams`) before
   * refetching the grid data.
   */
  onSubmit: (dataApi: DataApi, scope: Record<string, unknown>) => void;
}

export type EditableOverlayProps = EditableOverlayOwnProps & EditableOverlayDispatchProps;

// Single shared math instance to satisfy `ColumnDetailsConfig`; the integer/text/dropdown
// field branches used here don't actually evaluate calculations.
const math = globalMath.create(globalMath.all) as globalMath.MathJsStatic;

export const EditableOverlay = ({ configApi, onSubmit }: EditableOverlayProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [fields, setFields] = useState<EditableOverlayField[]>([]);
  const [dataApi, setDataApi] = useState<DataApi | null>(null);
  // entered values, keyed by dataIndex; passed to `columnConfigToEditableField` as `data`
  const [values, setValues] = useState<BasicPivotItem>({} as BasicPivotItem);
  // tracks which field is currently in its editing (vs. display) state
  const [openKey, setOpenKey] = useState<string | null>(null);

  useEffect(() => {
    if (isLoading) {
      return;
    }
    setIsLoading(true);
    Axios.get(configApi.url, {
      params: configApi.params,
    })
      .then((resp) => {
        const parsed = zEditableOverlay.parse(resp.data.data);
        setFields(parsed.fields);
        setDataApi(parsed.dataApi);
      })
      .catch((error) => {
        logError('[EditableOverlay] failed to load config', error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [configApi]);

  const handleDropdownChange = (selection: ValueSelection, item: DividedColumnDetailsSectionColumn) => {
    setValues((prev) => ({ ...prev, [item.dataIndex]: selection.value }));
    setOpenKey(null);
  };

  const renderedFields = useMemo(() => {
    return fields.map((field) => {
      const item = field as unknown as DividedColumnDetailsSectionColumn;
      const columnDetails: ColumnDetailsConfig = {
        item,
        fieldId: item.dataIndex,
        styleId: '',
        data: values,
        dropdownOptions: {},
        dependentCalcs: {},
        mathInstance: math,
        updateCoordinateMap: undefined,
        handleDropdownChange,
        onCloseCallback: () => setOpenKey(null),
        onClickCallback: (key) => setOpenKey(key),
        openDropdownKey: openKey,
      };
      return (
        <div className={styles.field} key={item.dataIndex}>
          <span className={styles.label}>{item.text}</span>
          {columnConfigToEditableField(columnDetails)}
        </div>
      );
    });
  }, [fields, values, openKey]);

  if (isLoading) {
    return <Overlay type="loading" visible={true} />;
  }

  return (
    <div className={styles.container} data-qa="editable-overlay">
      <div className={styles.fields}>{renderedFields}</div>
      <SquareActionButton
        text="Submit"
        dataQa="editable-overlay-submit"
        disabled={dataApi == null}
        onClick={() => dataApi && onSubmit(dataApi, { ...values })}
      />
    </div>
  );
};

export default EditableOverlay;
